'use client';

import { useState, useMemo } from 'react';
import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import { Delegation } from '@drift-labs/icons';
import Select from 'src/components/primitives/Select/Select';
import { AccountData } from 'src/stores/useDriftAccountsStore';
import { WALLET_OPTION_VALUE } from 'src/constants/constants';
import { abbreviateAddress } from 'src/utils/strings';
import NumLib from 'src/utils/NumLib';
import { WithClassName } from 'src/utils/UtilityTypes';
import { cj } from 'src/utils/classNames';
import { useAllAccountsMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';

interface CollateralSourceOption {
	value: string;
	label: string;
	displayValue: string;
	isDelegated?: boolean;
	poolId?: number;
}

interface CollateralSourceSelectorProps extends WithClassName {
	options: AccountData[];
	onChange: (accountId: string, poolId?: number) => void;
	initialSelection?: string;
	includeDelegates?: boolean;
	placeholderText?: string;
	disabled?: boolean;
	walletBalance?: BigNum;
	label?: string;
}

export function CollateralSourceSelector({
	options,
	onChange,
	initialSelection,
	includeDelegates = false,
	placeholderText = 'Select source',
	className,
	walletBalance,
	label = 'Deposit to',
}: CollateralSourceSelectorProps) {
	const defaultSelection = walletBalance
		? (initialSelection ?? WALLET_OPTION_VALUE)
		: (initialSelection ?? options?.[0]?.userKey);

	const [selectedValue, setSelectedValue] = useState(defaultSelection ?? '');

	const filteredOptions = useMemo(
		() =>
			options.filter((account) =>
				includeDelegates ? true : !account.isDelegatedTo
			),
		[options, includeDelegates]
	);

	const { data: allMarginInfos } = useAllAccountsMarginInfo();

	const selectOptions = useMemo(() => {
		const accountOptions: CollateralSourceOption[] = filteredOptions.map(
			(account) => {
				const displayLabel = account.isDelegatedTo
					? `${abbreviateAddress(account.authority)} (${account.userId})`
					: account.name;

				const marginInfo = allMarginInfos?.[account.userKey];
				const value = BigNum.from(
					marginInfo?.netUsdValue ?? 0,
					QUOTE_PRECISION_EXP
				).toNum();
				const displayValue = NumLib.formatNum.toNotionalDisplay(value);

				return {
					value: account.userKey,
					label: displayLabel,
					displayValue,
					isDelegated: account.isDelegatedTo,
					poolId: account.client?.getUserAccount()?.poolId,
				};
			}
		);

		const walletOption: CollateralSourceOption | null = walletBalance
			? {
					value: WALLET_OPTION_VALUE,
					label: 'Connected Wallet',
					displayValue: NumLib.formatNum.toNotionalDisplay(
						walletBalance.toNum()
					),
				}
			: null;

		return walletOption ? [walletOption, ...accountOptions] : accountOptions;
	}, [filteredOptions, walletBalance, allMarginInfos]);

	const selectedOption = selectOptions.find(
		(option) => option.value === selectedValue
	);

	const handleValueChange = (value: string) => {
		setSelectedValue(value);

		if (value === WALLET_OPTION_VALUE) {
			onChange(value);
		} else {
			const selectedAccount = filteredOptions.find(
				(account) => account.userKey === value
			);
			onChange(value, selectedAccount?.client?.getUserAccount()?.poolId);
		}
	};

	const isWalletSelected = selectedValue === WALLET_OPTION_VALUE;
	const selectedAccount = filteredOptions.find(
		(account) => account.userKey === selectedValue
	);

	const displayValue = () => {
		if (isWalletSelected) {
			return 'Connected Wallet';
		}
		if (selectedAccount) {
			return selectedAccount.isDelegatedTo
				? `${abbreviateAddress(selectedAccount.authority)} (${
						selectedAccount.userId
					})`
				: selectedAccount.name;
		}
		return placeholderText;
	};

	return (
		<Select.Root value={selectedValue} onValueChange={handleValueChange}>
			<Select.Trigger className={cj('w-full', className)} label={label}>
				{selectedOption && displayValue()}
			</Select.Trigger>

			<Select.Options scrollContainerClassName="max-h-[300px]">
				{selectOptions.length === 0 ? (
					<div className="text-text-secondary">No accounts available</div>
				) : (
					selectOptions.map((option) => (
						<Select.Option
							key={option.value}
							value={option.value}
							noSelectedCheckmark
						>
							<div className="flex items-center gap-2">
								<span>{option.label}</span>
								{option.isDelegated && (
									<Delegation className="h-4 w-4 text-text-secondary" />
								)}
							</div>
							<div className="text-text-secondary">
								<span>{option.displayValue}</span>
							</div>
						</Select.Option>
					))
				)}
			</Select.Options>
		</Select.Root>
	);
}
